// In eine Funktion gekapselt: mehrere Sections derselben Seite binden die Datei
// ein. So bleiben die Klassen lokal, und customElements.define ist geschützt.
(() => {
  /**
   * <lo-datenblatt> — Datengruppen auf Mobil zuklappen
   * ==================================================
   *
   * Im HTML stehen alle <details> offen: ohne JavaScript ist alles lesbar und
   * über die Seitensuche auffindbar. Erst hier, mit nachweislich laufendem
   * Skript, klappt es auf schmalen Bildschirmen alle Gruppen außer der ersten
   * zu — einmalig beim Laden, nicht bei jeder Größenänderung, damit nichts
   * zuschnappt, was jemand gerade liest.
   */
  class LoumeDatenblatt extends HTMLElement {
    connectedCallback() {
      if (this.hasAttribute('data-ready')) return;
      this.setAttribute('data-ready', '');

      if (!window.matchMedia('(max-width: 767px)').matches) return;

      var gruppen = this.querySelectorAll('details');
      for (var i = 1; i < gruppen.length; i++) {
        gruppen[i].open = false;
      }
    }
  }

  /**
   * <lo-kaufleiste> — Kaufleiste, die sich zurücknimmt
   * ==================================================
   *
   * Sichtbar nur, wenn
   *   · die Seite schon über den ersten Bildschirm hinaus gescrollt ist und
   *   · keines der Elemente aus data-verbergen gerade im Bild ist
   *     (Hero, Kaufbereich, Abschluss, Footer).
   *
   * Ohne IntersectionObserver bleibt die Leiste einfach weg — sie ist ein
   * Zusatz, kein Weg, der sonst fehlt.
   */
  class LoumeKaufleiste extends HTMLElement {
    connectedCallback() {
      if (this.beobachter || !('IntersectionObserver' in window)) return;

      var selektoren = this.getAttribute('data-verbergen') || '';
      var ziele = [];
      if (selektoren.trim()) {
        try {
          ziele = Array.prototype.slice.call(document.querySelectorAll(selektoren));
        } catch (e) {
          ziele = [];
        }
      }

      this.imBild = new Set();
      this.gescrollt = window.scrollY > window.innerHeight * 0.6;

      var self = this;
      this.beobachter = new IntersectionObserver(function (eintraege) {
        eintraege.forEach(function (eintrag) {
          if (eintrag.isIntersecting) {
            self.imBild.add(eintrag.target);
          } else {
            self.imBild.delete(eintrag.target);
          }
        });
        self.aktualisieren();
      });
      this.selektoren = selektoren;
      this.beobachten(ziele);

      this.beimScrollen = function () {
        var gescrollt = window.scrollY > window.innerHeight * 0.6;
        if (gescrollt !== self.gescrollt) {
          self.gescrollt = gescrollt;
          self.aktualisieren();
        }
      };
      window.addEventListener('scroll', this.beimScrollen, { passive: true });

      // Theme-Editor: neu gerenderte Sections sind neue Knoten und müssen
      // neu beobachtet werden.
      this.beimNeuladen = function () {
        self.beobachten();
      };
      document.addEventListener('shopify:section:load', this.beimNeuladen);

      // Erst jetzt aus dem Layout holen; sichtbar wird sie über data-sichtbar.
      this.removeAttribute('hidden');
      this.aktualisieren();
    }

    beobachten(ziele) {
      if (!this.beobachter) return;
      if (!ziele) {
        try {
          ziele = this.selektoren ? Array.prototype.slice.call(document.querySelectorAll(this.selektoren)) : [];
        } catch (e) {
          ziele = [];
        }
      }
      this.beobachter.disconnect();
      this.imBild.clear();
      var self = this;
      ziele.forEach(function (ziel) {
        self.beobachter.observe(ziel);
      });
    }

    disconnectedCallback() {
      if (this.beobachter) this.beobachter.disconnect();
      if (this.beimScrollen) window.removeEventListener('scroll', this.beimScrollen);
      if (this.beimNeuladen) document.removeEventListener('shopify:section:load', this.beimNeuladen);
      this.beobachter = null;
    }

    aktualisieren() {
      // Bewusst ohne Ausnahme für den Fokus: nach dem Schließen des
      // Warenkorbs setzt Dawn den Fokus zurück auf den Leistenknopf, und die
      // Leiste bliebe sonst über Kaufbereich und Footer stehen. Wird sie
      // ausgeblendet, gibt `inert` den Fokus frei.
      var zeigen = this.gescrollt && this.imBild.size === 0;

      if (zeigen) {
        this.setAttribute('data-sichtbar', '');
        this.inert = false;
      } else {
        this.removeAttribute('data-sichtbar');
        this.inert = true;
      }
    }
  }

  // Gemeinsame Leitung der Filme mit erstem Bild: Es lädt immer nur einer.
  // Wer schon lädt, lädt fertig; der nächste wartet. Nur ein Film, der zur
  // Hälfte im Bild ist, verdrängt einen, der bloß in der Nähe vorlädt. Der
  // Case-Film liegt direkt unter dem Akku-Film: beide luden sonst zugleich, und
  // der Akku-Film schaffte bei 4G seine Frist nicht. Wer aufgibt, hält die
  // Leitung, bis sein Schlussbild steht — sonst verdrängte es der Film des
  // Nachbarn. Nach einem Aufgeben wegen langsamen Netzes (netzLangsam) lädt
  // keiner mehr vor, nur noch der Film im Bild: auf so einer Leitung schafft
  // es auch der nächste nicht, und es spart Datenvolumen. Meldet der Browser
  // Datensparmodus oder ein langsames Netz (Chrome, Android; Safari kennt
  // navigator.connection nicht), gilt das von Anfang an.
  var filme = new Set();
  var verbindung = navigator.connection;
  var netzLangsam = !!(verbindung && (verbindung.saveData || /2g|3g/.test(verbindung.effectiveType || '')));
  function andererLaedt(ich) {
    var wer = null;
    filme.forEach(function (f) {
      if (!wer && f !== ich && f.leitung.laedt()) wer = f;
    });
    return wer;
  }
  // Leitung frei geworden (oder neu verteilt): die Filme im Bild zuerst.
  function leitungVerteilen() {
    Array.from(filme)
      .sort(function (a, b) { return b.leitung.imBild() - a.leitung.imBild(); })
      .forEach(function (f) { f.leitung.weiter(); });
  }

  /**
   * <lo-einmal-video> — ein Video, das abläuft und stehen bleibt
   * =============================================================
   *
   * Kommt der Kasten zur Hälfte ins Bild, spielt das Video einmal ab und bleibt
   * auf dem letzten Bild stehen (mobil eine eigene Datei aus data-src-mobil).
   * Ist die Bildfläche höher als das Fenster (Handy quer), genügt es, wenn sie
   * die halbe Fensterhöhe füllt — zur Hälfte käme sie dort nie ins Bild.
   * Verlässt der Kasten den Bildschirm ganz, springt es an den Anfang zurück;
   * kommt er wieder zur Hälfte ins Bild, läuft es erneut. Der Rahmen
   * (data-video-rahmen) bekommt dabei „ist-gestartet" neu gesetzt, damit eine
   * Zeile darauf jedes Mal wieder einblendet.
   *
   * Welche Datei, entscheidet die Breite beim Laden, nicht beim Einrichten: Wird
   * das Handy quer gedreht (oder das Fenster über 767px gezogen), wechselt das
   * <picture> darunter auf die andere Fassung — dann auch das Video, solange es
   * nicht läuft; ein laufender Film behält seine Datei bis zum Zurücksetzen.
   *
   * Mit erstem Bild (data-startbild) — so auf der Dream-Dots-Seite:
   *   Im HTML steht Bild 0 des Films. Es lädt, sobald das Skript läuft (wie
   *   früher das Poster), nicht erst kurz vor dem Kasten — bei langsamem Netz
   *   stand sonst lange ein leerer Kasten da. Das Video darüber wird erst
   *   sichtbar, wenn es selbst ein Bild hat (loadeddata) — dasselbe Bild, der
   *   Wechsel ist unsichtbar. Die Datei lädt, solange der Kasten höchstens einen
   *   Bildschirm entfernt ist und das erste Bild da ist (preload=auto); ist er
   *   weiter weg, ohne dass der Film lief, bricht das Laden ab — die Leitung
   *   gehört dann den Kästen, auf die der Besucher zuscrollt. Es lädt immer
   *   nur ein Film; einer im Bild geht einem bloß nahen vor (leitungVerteilen).
   *   Abgespielt wird,
   *   wenn der Kasten zur Hälfte im Bild ist, <lo-reveal> ihn ganz eingeblendet
   *   hat und genug geladen ist, um ohne Anhalten durchzulaufen: ganz, oder es
   *   lädt mindestens anderthalbmal so schnell, wie es spielt. Lädt der Browser
   *   vor play() von sich aus nicht weiter (iOS lädt nur die Metadaten), hilft
   *   Warten nicht — dann startet es gleich.
   *
   *   Kann der Film nicht laufen, blendet das Schlussbild aus dem <template>
   *   über (0,9 s), die Zeilen stehen wie ohne Film, und das Laden bricht ab:
   *     - play() abgelehnt (etwa iOS-Stromsparmodus),
   *     - nach 5 s zur Hälfte im Bild noch nicht genug geladen (langsames Netz).
   *       Nach 1,5 s wird geschätzt: Reicht das Tempo auch mit doppelter Reserve
   *       nicht bis zur Frist, gibt es gleich auf, sonst lädt das Schlussbild
   *       schon mit. Geschätzt wird nur, wenn der Browser den Puffer
   *       fortlaufend meldet (Chrome); WebKit meldet einen stehenden
   *       Zwischenstand, dort entscheidet allein die Frist. Die Frist zählt
   *       nur, solange der Kasten zur Hälfte im Bild ist — wer die Überschrift
   *       darüber liest, verliert den Film nicht.
   *   Das Schlussbild ist das gestaltete Foto, auf dem die Zeilen gemessen
   *   sind; die Anfangsszene allein erzählt nichts. Es bleibt dann stehen.
   *   Ohne IntersectionObserver steht gleich das Schlussbild. Bei „Bewegung
   *   reduzieren" und ohne JavaScript wählt schon das <picture> das
   *   Schlussbild (eigene <source>) — hier passiert dann nichts.
   *
   * Ohne erstes Bild (bisheriges Verhalten): Im HTML liegt das Schlussbild, das
   * Video darüber bekommt seine Quelle erst bei halber Sichtbarkeit und spielt
   * sofort. Scheitert play(), verschwindet es wieder und das Foto bleibt.
   *
   * Wird das Bild im Lauf des Films hell (data-hell-ab, Sekunden Filmzeit),
   * bekommt der Rahmen ab dann „ist-hell" — gekoppelt an die Filmzeit, nicht an
   * die Uhr: hält das Video zum Nachladen an, wartet auch die Zeile darauf.
   *
   * Theme-Editor: Wird die Section verschoben, nimmt disconnectedCallback alle
   * Lauscher ab und setzt das Video zurück; beim Wiedereinhängen beginnt es neu.
   * Hat der Film aufgegeben, bleibt es beim Schlussbild.
   */
  class LoumeEinmalVideo extends HTMLElement {
    connectedCallback() {
      if (this.lauscher || this.zustand === 'aus') return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var video = this.querySelector('video');
      if (!video) return;
      var self = this;
      var schmal = window.matchMedia('(max-width: 767px)');
      function quelleJetzt() {
        return (schmal.matches && self.dataset.srcMobil) || self.dataset.src;
      }
      if (!quelleJetzt()) return;

      var startbild = this.hasAttribute('data-startbild');
      var rahmen = (this.rahmen = this.closest('[data-video-rahmen]'));
      var reveal = this.closest('lo-reveal');
      this.video = video;
      this.startbild = startbild;
      // bereit → wartet (halb im Bild) → laeuft; aus (Schlussbild, endgültig)
      this.zustand = 'bereit';

      if (!('IntersectionObserver' in window)) {
        if (startbild) {
          this.zustand = 'aus';
          this.schlussbildZeigen();
        }
        return;
      }

      // Alle Lauscher hängen an diesem Signal: disconnectedCallback und
      // aufgeben() nehmen sie mit einem Mal ab.
      this.lauscher = new AbortController();
      var an = { signal: this.lauscher.signal };

      if (rahmen) rahmen.classList.add('ist-video');
      if (startbild) {
        // Sichtbar erst mit eigenem Bild; zwei Animationsbilder später ist es
        // gemalt. Inzwischen abgebrochen (readyState 0)? Dann nicht.
        video.addEventListener('loadeddata', function () {
          requestAnimationFrame(function () {
            requestAnimationFrame(function () {
              if (video.readyState >= 2) self.classList.add('ist-bereit');
            });
          });
        }, an);
      } else {
        this.classList.add('ist-bereit');
      }
      video.addEventListener('playing', function () {
        if (rahmen) rahmen.classList.add('ist-gestartet');
      }, an);

      var hellAb = parseFloat(this.dataset.hellAb);
      if (rahmen && hellAb > 0) {
        video.addEventListener('timeupdate', function () {
          rahmen.classList.toggle('ist-hell', video.currentTime >= hellAb);
        }, an);
      }

      var geladen = false;
      var quelle = '';
      var imBild = false;
      var nah = false;
      var ladebeginn = 0;
      var ruhtSeit = 0;
      var erstesBild = startbild && rahmen ? rahmen.querySelector('picture img') : null;
      var wartetAufBild = false;
      var warGanz = false;

      // Das erste Bild gleich laden, wie früher das Poster: im HTML steht es
      // lazy, und das lädt erst kurz vor dem Kasten — bei langsamem Netz zu spät.
      if (erstesBild && erstesBild.loading === 'lazy') erstesBild.loading = 'eager';

      function gepuffert() {
        return video.buffered.length ? video.buffered.end(video.buffered.length - 1) : 0;
      }

      // Wie oft und wann der gemeldete Puffer sich zuletzt geändert hat. WebKit
      // meldet buffered beim ersten Laden nicht fortlaufend: erst 0, dann einen
      // Zwischenstand (Desktop 1,13 s, iPhone 2,33 s), der stehen bleibt, bis
      // alles da ist. Ein Tempo daraus ist viel zu niedrig — am Desktop bei 4G
      // gab der Akku-Film deshalb nach 1,5 s auf, obwohl er 1,5 s später ganz
      // geladen war. Chrome meldet mit jedem progress einen neuen Stand.
      var puffer = { ende: 0, seit: 0, wechsel: 0 };
      function pufferMerken() {
        var ende = gepuffert();
        if (ende === puffer.ende) return;
        puffer.ende = ende;
        puffer.seit = performance.now();
        puffer.wechsel++;
      }
      function pufferVergessen() {
        puffer = { ende: 0, seit: 0, wechsel: 0 };
      }

      function ganzGeladen() {
        var dauer = video.duration;
        return !!dauer && gepuffert() >= dauer - 0.1;
      }

      function laden() {
        if (geladen) return;
        geladen = true;
        quelle = quelleJetzt();
        ladebeginn = performance.now();
        ruhtSeit = 0;
        warGanz = false;
        pufferVergessen();
        if (startbild) video.preload = 'auto';
        video.src = quelle;
      }

      // Laden abbrechen und zurück auf „nichts geladen" — nie mitten im Film.
      function abbrechen() {
        if (!geladen || self.zustand === 'laeuft') return;
        geladen = false;
        ladebeginn = 0;
        ruhtSeit = 0;
        pufferVergessen();
        video.removeAttribute('src');
        video.load();
        if (startbild) {
          self.classList.remove('ist-bereit');
          window.setTimeout(leitungVerteilen, 0);
        }
      }

      // Erst das erste Bild, dann der Film: bei langsamem Netz teilten sie sich
      // sonst die Leitung, und der Kasten bliebe länger leer.
      function vorladen() {
        if (geladen || self.zustand === 'aus') return;
        if (!imBild && netzLangsam) return;
        // Ein anderer lädt: warten — außer dieser ist im Bild und der andere nur in der Nähe.
        var anderer = andererLaedt(self);
        if (anderer && imBild && !anderer.leitung.imBild()) anderer.leitung.zurueckstellen();
        if (andererLaedt(self)) return;
        if (erstesBild && !erstesBild.complete) {
          if (wartetAufBild) return;
          wartetAufBild = true;
          var weiter = function () {
            wartetAufBild = false;
            if (nah || imBild) vorladen();
          };
          erstesBild.addEventListener('load', weiter, { once: true, signal: an.signal });
          erstesBild.addEventListener('error', weiter, { once: true, signal: an.signal });
          return;
        }
        laden();
      }

      // <lo-reveal> blendet den Kasten beim Hereinscrollen ein (0,8 s). Erst
      // danach starten, sonst läuft der Anfang halb durchsichtig ab.
      function eingeblendet() {
        if (!startbild || !reveal || !reveal.hasAttribute('data-ready')) return true;
        return reveal.classList.contains('is-visible') && parseFloat(getComputedStyle(reveal).opacity) >= 0.99;
      }

      function genugGeladen() {
        if (!startbild) return true;
        if (!geladen) return false;
        if (ganzGeladen()) return true;
        // Der Browser lädt von sich aus nicht weiter (seit 300 ms Ruhe, nicht ganz geladen)
        if (ruhtSeit && video.networkState === video.NETWORK_IDLE && performance.now() - ruhtSeit >= 300) return true;
        var ende = gepuffert();
        var sekunden = (performance.now() - ladebeginn) / 1000;
        return ende >= 1 && sekunden > 0 && ende / sekunden >= 1.5;
      }

      // Schafft das bisherige Tempo die Frist auch mit doppelter Reserve nicht?
      // Dann lohnt kein Warten: der Film belegte nur die Leitung, die das
      // Schlussbild braucht. Unter einer Sekunde Laden gibt es keine Grundlage.
      // Geurteilt wird nur über einen fortlaufend gemeldeten Puffer: mindestens
      // zwei Stände über 0, der letzte höchstens 0,6 s alt. Sonst (WebKit, oder
      // das Netz steht) entscheidet die 5-s-Frist — lieber später aufs
      // Schlussbild als einen Film aufgeben, der gleich da wäre.
      function aussichtslos(restbisFrist) {
        var dauer = video.duration;
        if (!geladen || !dauer || !isFinite(dauer)) return false;
        var sekunden = (performance.now() - ladebeginn) / 1000;
        if (sekunden < 1) return false;
        pufferMerken();
        if (puffer.wechsel < 2 || performance.now() - puffer.seit > 600) return false;
        var ende = puffer.ende;
        if (!ende) return false;
        var tempo = ende / sekunden;
        if (tempo >= 1.5) return false;
        return (dauer - ende) / Math.max(tempo, 0.01) > 2 * restbisFrist;
      }

      function pruefen() {
        window.clearTimeout(self.nochmal);
        if (!imBild || self.zustand !== 'wartet') return;
        if (!eingeblendet()) {
          self.nochmal = window.setTimeout(pruefen, 150);
          return;
        }
        if (genugGeladen()) starten();
      }

      function fristenStarten() {
        self.vorwarnung = window.setTimeout(function () {
          if (self.zustand !== 'wartet') return;
          if (aussichtslos(3.5)) self.aufgeben(true);
          else self.schlussbildHolen();
        }, 1500);
        self.wartezeit = window.setTimeout(function () {
          if (self.zustand === 'wartet') self.aufgeben(true);
        }, 5000);
      }

      function fristenStoppen() {
        window.clearTimeout(self.wartezeit);
        window.clearTimeout(self.vorwarnung);
        window.clearTimeout(self.nochmal);
      }

      function starten() {
        fristenStoppen();
        self.zustand = 'laeuft';
        laden();
        var versuch = video.play();
        if (versuch && versuch.catch) {
          versuch.catch(function (fehler) {
            // pause() beim Hinausscrollen bricht play() ab — das ist kein Scheitern.
            if (fehler && fehler.name === 'AbortError') return;
            if (self.zustand === 'laeuft') self.aufgeben();
          });
        }
      }

      function zuruecksetzen() {
        fristenStoppen();
        if (self.zustand === 'bereit' || self.zustand === 'aus') return;
        if (geladen) {
          video.pause();
          video.currentTime = 0;
        }
        if (rahmen) rahmen.classList.remove('ist-gestartet', 'ist-hell');
        self.zustand = 'bereit';
        // Inzwischen andere Breite (Handy gedreht): beim nächsten Mal die passende Datei.
        if (geladen && quelle !== quelleJetzt()) {
          abbrechen();
          if (startbild && nah) vorladen();
        } else if (startbild && !nah && !ganzGeladen()) {
          // In einem Sprung weit weg (Sprunglink): der Vorlader sah noch den
          // laufenden Film und ließ ihn laden.
          abbrechen();
        }
      }

      if (schmal.addEventListener) {
        schmal.addEventListener('change', function () {
          if (!geladen || quelle === quelleJetzt()) return;
          if (self.zustand !== 'bereit' && self.zustand !== 'wartet') return;
          abbrechen();
          if (startbild && (nah || imBild)) vorladen();
          if (startbild) leitungVerteilen();
        }, an);
      }

      // Ganz geladen oder abgespielt: die Leitung wird frei.
      function fertigPruefen() {
        if (warGanz || !(video.ended || ganzGeladen())) return;
        warGanz = true;
        leitungVerteilen();
      }

      if (startbild) {
        this.leitung = {
          // Belegt die Leitung: lädt und ist nicht fertig — nicht, wenn der
          // Browser ruht (iOS vor play(): NETWORK_IDLE) oder der Download
          // scheiterte. Aufgegeben: bis das Schlussbild steht (imBild ist dann
          // der letzte Stand).
          laedt: function () {
            if (self.zustand === 'aus') return imBild && !self.schlussSichtbar;
            if (!geladen || video.error || video.ended || ganzGeladen()) return false;
            return video.networkState !== video.NETWORK_IDLE;
          },
          imBild: function () {
            return imBild;
          },
          zurueckstellen: function () {
            if (!imBild && self.zustand === 'bereit' && !ganzGeladen()) abbrechen();
          },
          weiter: function () {
            if (nah || imBild) vorladen();
          }
        };
        filme.add(this);

        video.addEventListener('progress', function () {
          ruhtSeit = 0;
          pufferMerken();
          fertigPruefen();
          pruefen();
        }, an);
        video.addEventListener('suspend', function () {
          ruhtSeit = performance.now();
          pufferMerken();
          fertigPruefen();
          leitungVerteilen();
          window.setTimeout(pruefen, 320);
        }, an);
        video.addEventListener('canplaythrough', function () {
          pufferMerken();
          fertigPruefen();
          pruefen();
        }, an);
        video.addEventListener('loadeddata', function () {
          pufferMerken();
          pruefen();
        }, an);
        video.addEventListener('ended', fertigPruefen, an);
        video.addEventListener('error', leitungVerteilen, an);

        // Höchstens einen Bildschirm entfernt: laden. Weiter weg, ohne dass der
        // Film lief und ganz geladen ist: Laden abbrechen.
        this.vorlader = new IntersectionObserver(function (eintraege) {
          nah = eintraege[eintraege.length - 1].isIntersecting;
          if (nah) vorladen();
          else if (self.zustand === 'bereit' && !ganzGeladen()) abbrechen();
        }, { rootMargin: '100% 0px' });
        this.vorlader.observe(this);
      }

      // „Zur Hälfte im Bild" heißt: halb sichtbar — oder, wenn die Bildfläche
      // höher ist als das Fenster, mindestens die halbe Fensterhöhe gefüllt. Am
      // Handy quer (Bild 4:5, Fenster 342px) kam sie sonst nie über 38 %: kein
      // Film, keine Zeile. rootBounds fehlt im Theme-Editor (fremder iframe), dann
      // gilt innerHeight. Die Stufen unter 0,5 melden das rechtzeitig.
      function halbImBild(eintrag) {
        if (eintrag.intersectionRatio >= 0.5) return true;
        if (!eintrag.isIntersecting) return false;
        var fenster = eintrag.rootBounds ? eintrag.rootBounds.height : window.innerHeight;
        return fenster > 0 && eintrag.intersectionRect.height >= fenster * 0.5;
      }

      this.beobachter = new IntersectionObserver(function (eintraege) {
        eintraege.forEach(function (eintrag) {
          if (halbImBild(eintrag)) {
            imBild = true;
            if (self.zustand === 'bereit') {
              self.zustand = 'wartet';
              if (startbild) {
                vorladen();
                fristenStarten();
              }
            }
            pruefen();
          } else if (!eintrag.isIntersecting) {
            imBild = false;
            zuruecksetzen();
          } else {
            imBild = false;
            // Nur noch ein Rand im Bild, und der Film hat nicht begonnen: Die
            // Fristen halten an und beginnen beim nächsten halben Hereinkommen
            // neu. Sonst gab die 5-s-Frist den Film auf, während der Besucher
            // die Überschrift darüber las, und er lief nie mehr.
            if (self.zustand === 'wartet') {
              fristenStoppen();
              self.zustand = 'bereit';
            }
          }
        });
        if (startbild) leitungVerteilen();
      }, { threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5] });
      this.beobachter.observe(this);
    }

    // Kein Film mehr, auch nicht beim nächsten Hereinscrollen. langsam: weil
    // das Netz nicht reichte (nicht: play() abgelehnt).
    aufgeben(langsam) {
      window.clearTimeout(this.wartezeit);
      window.clearTimeout(this.vorwarnung);
      window.clearTimeout(this.nochmal);
      this.zustand = 'aus';
      if (this.beobachter) this.beobachter.disconnect();
      if (this.vorlader) this.vorlader.disconnect();
      if (this.lauscher) this.lauscher.abort();
      if (langsam) netzLangsam = true;
      if (this.startbild) {
        this.video.removeAttribute('src');
        this.video.load();
        // Die Zeilen gleich zeigen (auf dem ersten Bild), nicht erst mit dem
        // Schlussbild — bei langsamem Netz kam das mitunter erst nach Sekunden.
        if (this.rahmen) {
          this.rahmen.classList.remove('ist-video', 'ist-gestartet', 'ist-hell');
          this.rahmen.classList.add('ist-aus');
        }
        leitungVerteilen();
        this.schlussbildZeigen();
      } else {
        this.classList.remove('ist-bereit');
        if (this.rahmen) this.rahmen.classList.remove('ist-video', 'ist-gestartet', 'ist-hell');
      }
    }

    // Schlussbild aus dem <template> in den Kasten holen — noch unsichtbar, es lädt.
    schlussbildHolen() {
      if (this.schluss) return this.schluss;
      var vorlage = this.querySelector('template');
      if (!vorlage || !vorlage.content.firstElementChild) return null;
      this.schluss = vorlage.content.firstElementChild.cloneNode(true);
      // Es wird gebraucht, solange der Kasten im Bild ist: vor die übrigen Bilder
      // der Seite (bei 3G wartete es sonst hinter einem 500-KB-Foto weiter unten).
      var bild = this.schluss.querySelector('img');
      if (bild) bild.setAttribute('fetchpriority', 'high');
      this.appendChild(this.schluss);
      return this.schluss;
    }

    // Schlussbild über das erste Bild blenden (CSS: ist-schluss), erst wenn es
    // geladen und dekodiert ist — sonst blendete es ins Leere.
    schlussbildZeigen() {
      var self = this;
      if (!this.schlussbildHolen()) return;
      var img = this.schluss.querySelector('img');
      function zeigen() {
        if (self.zustand !== 'aus') return;
        self.classList.add('ist-bereit');
        if (self.rahmen) {
          self.rahmen.classList.remove('ist-video', 'ist-gestartet', 'ist-hell');
          self.rahmen.classList.add('ist-schluss');
        }
        self.schlussSichtbar = true;
        self.leitungFreigeben();
      }
      if (!img || !img.decode) {
        zeigen();
        return;
      }
      img.decode().then(zeigen, function () {
        if (img.complete && img.naturalWidth) zeigen();
        else self.leitungFreigeben();
      });
    }

    leitungFreigeben() {
      if (filme.delete(this)) leitungVerteilen();
    }

    disconnectedCallback() {
      window.clearTimeout(this.wartezeit);
      window.clearTimeout(this.vorwarnung);
      window.clearTimeout(this.nochmal);
      if (this.beobachter) this.beobachter.disconnect();
      if (this.vorlader) this.vorlader.disconnect();
      this.beobachter = null;
      this.vorlader = null;
      if (this.lauscher) this.lauscher.abort();
      this.lauscher = null;
      this.leitungFreigeben();
      // Beim Schlussbild bleibt es; sonst zurück auf Anfang, als wäre nichts
      // geladen — connectedCallback richtet beim Wiedereinhängen neu ein.
      if (!this.video || this.zustand === 'aus') return;
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
      this.classList.remove('ist-bereit');
      if (this.rahmen) this.rahmen.classList.remove('ist-video', 'ist-gestartet', 'ist-hell');
    }
  }

  /**
   * <lo-geraeusche> — Geräusche im Bild der Vollbild-Section
   *
   * Setzt ist-aktiv, sobald ein gutes Drittel des Bilds im Blick ist; dann
   * laufen Einblenden, Schallringe und das Leiserwerden (CSS). Ist das Bild
   * ganz hinausgescrollt, fällt die Klasse wieder weg, beim nächsten Mal
   * beginnt es von vorn. data-bereit versteckt die Plaketten erst, wenn dieses
   * Skript läuft; ohne JS und bei „Bewegung reduzieren" bleiben sie stehen.
   *
   * Die Passform-Section nutzt dasselbe Element für Zeile und Größen-Karte.
   */
  class LoumeGeraeusche extends HTMLElement {
    connectedCallback() {
      if (this.beobachter) return;
      if (!('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      this.setAttribute('data-bereit', '');
      var self = this;
      // Ein gutes Drittel des Bilds — oder, bei sehr flachen Fenstern, in
      // denen nie ein Drittel hineinpasst, die halbe Fensterhöhe. Sonst blieben
      // Zeile und Plaketten dort für immer unsichtbar. rootBounds fehlt im
      // Theme-Editor (fremder iframe), dann gilt innerHeight.
      this.beobachter = new IntersectionObserver(function (eintraege) {
        eintraege.forEach(function (eintrag) {
          var fenster = eintrag.rootBounds ? eintrag.rootBounds.height : window.innerHeight;
          if (eintrag.intersectionRatio >= 0.35 || eintrag.intersectionRect.height >= fenster * 0.5) {
            self.classList.add('ist-aktiv');
          } else if (!eintrag.isIntersecting) {
            self.classList.remove('ist-aktiv');
          }
        });
      }, { threshold: [0, 0.1, 0.2, 0.35, 0.5] });
      this.beobachter.observe(this);
    }

    // Beobachter freigeben, damit ein im DOM verschobenes Element (Theme-Editor:
    // Section umsortieren) beim nächsten connectedCallback neu beobachtet.
    disconnectedCallback() {
      if (this.beobachter) this.beobachter.disconnect();
      this.beobachter = null;
    }
  }

  /**
   * <lo-loop-video> — stummes Loop-Video über einem Foto
   *
   * Setzt die Quelle erst, wenn das Element in die Nähe kommt (Handy: eigene,
   * kleinere Datei aus data-src-mobil), spielt nur, solange es im Blick ist,
   * und blendet sich erst ein, wenn das Video wirklich läuft (ist-bereit).
   * Bei „Bewegung reduzieren", ohne IntersectionObserver oder wenn play()
   * scheitert, bleibt es unsichtbar — dann steht das Foto darunter.
   */
  class LoumeLoopVideo extends HTMLElement {
    connectedCallback() {
      if (this.beobachter) return;
      if (!('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      var video = this.querySelector('video');
      if (!video) return;
      var mobil = window.matchMedia('(max-width: 767px)').matches;
      var quelle = (mobil && this.dataset.srcMobil) || this.dataset.src;
      if (!quelle) return;

      var self = this;
      var geladen = false;
      video.addEventListener('playing', function () {
        self.classList.add('ist-bereit');
      });

      function abspielen() {
        if (!geladen) {
          video.src = quelle;
          geladen = true;
        }
        var versuch = video.play();
        if (versuch && versuch.catch) {
          versuch.catch(function () {
            self.classList.remove('ist-bereit');
          });
        }
      }

      this.beobachter = new IntersectionObserver(function (eintraege) {
        eintraege.forEach(function (eintrag) {
          if (eintrag.isIntersecting) abspielen();
          else if (geladen) video.pause();
        });
      }, { rootMargin: '200px 0px' });
      this.beobachter.observe(this);
    }

    disconnectedCallback() {
      if (this.beobachter) this.beobachter.disconnect();
      this.beobachter = null;
    }
  }

  /**
   * <lo-balken> — Laufzeit-Balken wachsen beim Hereinscrollen
   *
   * Umschließt die Balkenliste der Laufzeit-Karte (loume-dd-akku). Versteckt
   * wird erst hier (data-bereit), und nur, solange niemand die Balken sieht:
   * entweder ist die Karte (<lo-reveal>) gerade durchsichtig, oder sie liegt
   * außerhalb des Fensters. Stehen die Balken beim Start des Skripts schon
   * sichtbar im Bild (Sprung zu #laufzeit, Neuladen weiter unten), bleibt es
   * beim fertigen Bild — nichts verschwindet vor den Augen.
   *
   * Sind die Balken ganz im Bild (unteres Zehntel ausgenommen; in sehr flachen
   * Fenstern genügt die halbe Fensterhöhe) und hat <lo-reveal> fertig
   * eingeblendet, kommt ist-aktiv — den Ablauf macht das CSS
   * (loume-dd-akku.css). Einmal je Seitenaufruf. Danach fallen beide Marken
   * wieder weg: der Endzustand ist dann genau der ohne Skript.
   *
   * Das Einblenden dauert 0,8 s. Wer in der Zeit weiterscrollt, bis die Balken
   * wieder aus dem Bild sind, verpasst nichts: der Beobachter bleibt bis zum
   * Start und führt imBild; gestartet wird nur im Bild, sonst warten die
   * Balken versteckt, bis sie wieder ganz hereinkommen (wie der Case-Film).
   *
   * Ohne IntersectionObserver und bei „Bewegung reduzieren" passiert nichts.
   * Theme-Editor: disconnectedCallback zeigt alles und nimmt Beobachter und
   * Uhr ab; beim Wiedereinhängen geht es von vorn los, wenn es noch nicht lief.
   */
  // Nur Rückfall fürs Aufräumen, falls sich die Zeiten nicht aus dem CSS lesen
  // lassen: 2 Balken, 180 ms versetzt, je 1000 ms + Spanne ab 1000 für 700 ms.
  var BALKEN_RUECKFALL = 180 + 1700;

  // „0.18s, 1.03s" / „850ms" → [180, 1030] / [850]
  function balkenZeiten(wert) {
    return String(wert || '')
      .split(',')
      .map(function (t) {
        t = t.trim();
        var n = parseFloat(t);
        return /ms$/.test(t) ? n : n * 1000;
      });
  }

  class LoumeBalken extends HTMLElement {
    // Was nacheinander kommt (bekommt --lo-balken-i) und welche Teile die
    // Übergänge tragen (daraus die Dauer fürs Aufräumen). <lo-wecker> nennt
    // hier seine eigenen Teile und erbt den Rest.
    get schritte() {
      return '.loume-dd-akku__zeile';
    }

    get teile() {
      return '.loume-dd-akku__fest, .loume-dd-akku__wert, .loume-dd-akku__offen';
    }

    // Ab welchem Anteil im Bild es losgeht, und bei welchen Anteilen der
    // Beobachter meldet. Die flachen Balken müssen ganz im Bild sein, dafür
    // genügen wenige Stufen.
    get anteil() {
      return 0.99;
    }

    get schwellen() {
      return [0, 0.5, 0.75, 0.99, 1];
    }

    connectedCallback() {
      if (this.beobachter || this.gelaufen) return;
      if (!('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Verdeckt zählt nur, was wirklich durchsichtig ist: <lo-reveal> blendet
      // eine schon gezeichnete Karte erst über 0,8 s aus (data-ready), beim
      // Sprung zu #laufzeit stünde sie sonst sichtbar ohne Balken da.
      var reveal = (this.reveal = this.closest('lo-reveal'));
      var verdeckt = reveal && parseFloat(getComputedStyle(reveal).opacity) < 0.05;
      if (!verdeckt) {
        var r = this.getBoundingClientRect();
        if (r.bottom > 0 && r.top < window.innerHeight && r.height > 0) {
          this.gelaufen = true;
          return;
        }
      }

      var zeilen = this.querySelectorAll(this.schritte);
      for (var i = 0; i < zeilen.length; i++) zeilen[i].style.setProperty('--lo-balken-i', i);
      this.setAttribute('data-bereit', '');
      this.imBild = false;

      // Ganz im Bild: mindestens 99 % (this.anteil) im Fenster ohne sein
      // unteres Zehntel — oder, wo das Fenster dafür zu flach ist, die halbe
      // Fensterhöhe gefüllt. rootBounds fehlt im Theme-Editor (fremder iframe),
      // dann gilt innerHeight.
      var self = this;
      var anteil = this.anteil;
      this.beobachter = new IntersectionObserver(
        function (eintraege) {
          var eintrag = eintraege[eintraege.length - 1];
          var fenster = eintrag.rootBounds ? eintrag.rootBounds.height : window.innerHeight;
          self.imBild =
            eintrag.isIntersecting &&
            (eintrag.intersectionRatio >= anteil || (fenster > 0 && eintrag.intersectionRect.height >= fenster * 0.5));
          if (self.imBild) self.wartenAufReveal();
          else window.clearTimeout(self.uhr); // nicht mehr nachfragen, bis sie wiederkommen
        },
        { rootMargin: '0px 0px -10% 0px', threshold: this.schwellen }
      );
      this.beobachter.observe(this);
    }

    // Zur Sicherheit beim Start noch einmal selbst gemessen: der Beobachter
    // meldet ein Bild später. Großzügiger als er (zur Hälfte im ganzen
    // Fenster), damit beide sich nie widersprechen, solange nichts scrollt —
    // sonst blieben die Balken versteckt stehen.
    nochImBild() {
      var r = this.getBoundingClientRect();
      var hoehe = window.innerHeight;
      var sichtbar = Math.min(r.bottom, hoehe) - Math.max(r.top, 0);
      return r.height > 0 && sichtbar >= Math.min(r.height, hoehe * 0.9) * 0.5;
    }

    // <lo-reveal> blendet die Karte in 0,8 s ein. Erst danach wachsen die
    // Balken, sonst liefe der Anfang halb durchsichtig ab. Gefragt wird nur,
    // solange die Balken im Bild sind; verlassen sie es, kommt der nächste
    // Anlauf vom Beobachter, wenn sie wieder ganz hereinkommen.
    wartenAufReveal() {
      var self = this;
      var reveal = this.reveal;
      window.clearTimeout(this.uhr);
      if (!this.isConnected || this.gelaufen || !this.imBild) return;
      var fertig =
        !reveal ||
        !reveal.hasAttribute('data-ready') ||
        (reveal.classList.contains('is-visible') && parseFloat(getComputedStyle(reveal).opacity) >= 0.99);
      if (!fertig) {
        this.uhr = window.setTimeout(function () {
          self.wartenAufReveal();
        }, 100);
        return;
      }
      if (!this.nochImBild()) return;
      this.gelaufen = true;
      if (this.beobachter) this.beobachter.disconnect();
      this.beobachter = null;
      // Versteckten Stand festschreiben, damit der Übergang von dort aus läuft.
      void this.offsetWidth;
      this.classList.add('ist-aktiv');
      this.uhr = window.setTimeout(function () {
        self.aufraeumen();
      }, this.dauer() + 300);
    }

    // Wie lange laufen die Übergänge (Verzögerung + Dauer, das späteste)? Aus
    // dem CSS gelesen, damit das Aufräumen keinen Übergang abschneidet, wenn
    // dort die Zeiten wachsen.
    dauer() {
      var teile = this.querySelectorAll(this.teile);
      var ende = 0;
      for (var i = 0; i < teile.length; i++) {
        var stil = getComputedStyle(teile[i]);
        var dauern = balkenZeiten(stil.transitionDuration);
        var verzoegerungen = balkenZeiten(stil.transitionDelay);
        for (var k = 0; k < dauern.length; k++) {
          ende = Math.max(ende, (verzoegerungen[k % verzoegerungen.length] || 0) + dauern[k]);
        }
      }
      return ende > 0 && isFinite(ende) ? ende : BALKEN_RUECKFALL;
    }

    aufraeumen() {
      window.clearTimeout(this.uhr);
      this.removeAttribute('data-bereit');
      this.classList.remove('ist-aktiv');
    }

    disconnectedCallback() {
      if (this.beobachter) this.beobachter.disconnect();
      this.beobachter = null;
      this.aufraeumen();
    }
  }

  /**
   * <lo-wecker> — die Wecker der Wecker-Section werden beim Hereinscrollen
   * „gestellt“ (loume-dd-wecker): erst leuchten die Tage eines Weckers
   * nacheinander auf, dann springt sein Schalter an. Das Warten auf
   * <lo-reveal> und das Aufräumen wie bei <lo-balken>; die Zeiten stehen in
   * loume-dd-wecker.css.
   *
   * Los geht es schon bei 85 % im Bild: Die Karte ist hoch, und wer mit ihrem
   * unteren Rand knapp über der Fensterkante stehen blieb, sah sonst die
   * Wecker auf „aus“ — am Handy (390×844) bis zum Weiterscrollen, weil die
   * Karte dort nie die halbe Fensterhöhe füllt. Gemessen mit
   * tools/visual-diff/wecker-check.mjs --modus=halt.
   */
  class LoumeWecker extends LoumeBalken {
    get schritte() {
      return '.loume-dd-wecker__schritt';
    }

    get teile() {
      return '.loume-dd-wecker__schritt';
    }

    get anteil() {
      return 0.85;
    }

    // In 5-%-Stufen, damit auch die halbe Fensterhöhe (flache Fenster, Handy
    // quer) nicht zwischen zwei Meldungen fällt.
    get schwellen() {
      var stufen = [];
      for (var i = 0; i <= 20; i++) stufen.push(i / 20);
      return stufen;
    }
  }

  if (!customElements.get('lo-balken')) customElements.define('lo-balken', LoumeBalken);
  if (!customElements.get('lo-wecker')) customElements.define('lo-wecker', LoumeWecker);
  if (!customElements.get('lo-datenblatt')) customElements.define('lo-datenblatt', LoumeDatenblatt);
  if (!customElements.get('lo-loop-video')) customElements.define('lo-loop-video', LoumeLoopVideo);
  if (!customElements.get('lo-geraeusche')) customElements.define('lo-geraeusche', LoumeGeraeusche);
  if (!customElements.get('lo-einmal-video')) customElements.define('lo-einmal-video', LoumeEinmalVideo);
  if (!customElements.get('lo-kaufleiste')) customElements.define('lo-kaufleiste', LoumeKaufleiste);
})();
